import React from 'react';
import { Users, Heart, Calendar, Mail, Clock, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

const Volunteer: React.FC = () => {
  const opportunities = [
    { title: 'Direct Animal Care', description: 'Walk dogs, clean kennels, provide enrichment for cats and dogs', icon: Heart },
    { title: 'Shelter Maintenance', description: 'General cleaning, trail maintenance, garden weeding, etc.', icon: Sparkles },
    { title: 'Committee Work', description: 'Special event committees and development task groups', icon: Users },
    { title: 'Medical Team Assistance', description: 'Licensed professionals can volunteer with our medical staff', icon: Heart },
    { title: 'Administrative Support', description: 'Data entry, mailing correspondence, grant writing, and event prep', icon: Calendar },
    { title: 'Newsletter Stuffing', description: 'No training required! Help put The Paw Print newsletter into mailboxes', icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-cream-white">
      <section className="bg-gradient-to-r from-sage-green to-sky-blue py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-5xl text-white mb-4"
          >
            Volunteer With Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/90 text-xl max-w-3xl mx-auto"
          >
            Volunteers are a vital part of our work, allowing Animal Allies to save the lives of thousands of animals
          </motion.p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl p-8 shadow-md mb-8">
          <h2 className="font-display text-3xl text-deep-forest mb-6">Make a Difference</h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-warm-coral/10 rounded-xl p-6 text-center">
              <Clock className="text-warm-coral mx-auto mb-3" size={32} />
              <h3 className="font-semibold text-lg text-deep-forest mb-2">Requirements</h3>
              <p className="text-warm-gray">16+ years for general positions</p>
              <p className="text-warm-gray">6-month commitment for animal interaction</p>
            </div>
            <div className="bg-sky-blue/10 rounded-xl p-6 text-center">
              <Users className="text-sky-blue mx-auto mb-3" size={32} />
              <h3 className="font-semibold text-lg text-deep-forest mb-2">Join Our Team</h3>
              <p className="text-warm-gray">Flexible scheduling</p>
              <p className="text-warm-gray">Training provided</p>
            </div>
            <div className="bg-sage-green/10 rounded-xl p-6 text-center">
              <Heart className="text-sage-green mx-auto mb-3" size={32} fill="currentColor" />
              <h3 className="font-semibold text-lg text-deep-forest mb-2">Impact</h3>
              <p className="text-warm-gray">12,500+ volunteer hours annually</p>
              <p className="text-warm-gray">Directly save lives</p>
            </div>
          </div>

          <h3 className="font-semibold text-xl text-deep-forest mb-6">Volunteer Opportunities</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {opportunities.map((opp, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-cream-white rounded-xl p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-white p-2 rounded-lg">
                    <opp.icon className="text-warm-coral" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-deep-forest mb-2">{opp.title}</h4>
                    <p className="text-warm-gray text-sm">{opp.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-warm-gray mb-4">Ready to make a difference?</p>
            <a
              href="mailto:volunteer@animalallies.net"
              className="inline-flex items-center gap-2 bg-warm-coral hover:bg-warm-coral/90 text-white px-8 py-4 rounded-full font-semibold transition-colors"
            >
              <Mail size={20} />
              Contact Volunteer Coordinator
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Volunteer;